import { Map, List } from 'immutable'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  SUBTYPE_CONTENT_YOGA,
  SUBTYPE_CONTENT_FITNESS,
  SUBTYPE_CONTENT_MEDITATION,
  SUBTYPE_CONTENT_EVENT,
  TYPE_TAG_GENRE,
  TYPE_TAG_YOGA_DURATION,
  TYPE_TAG_YOGA_STYLE,
  TYPE_TAG_YOGA_LEVEL,
  TYPE_TAG_YOGA_FOCUS,
  TYPE_TAG_FITNESS_DURATION,
  TYPE_TAG_FITNESS_STYLE,
  TYPE_TAG_FITNESS_LEVEL,
  TYPE_TAG_FITNESS_FOCUS,
  TYPE_TAG_MEDITATION_DURATION,
  TYPE_TAG_MEDITATION_STYLE,
  TYPE_TAG_MEDITATION_LEVEL,
  TYPE_TAG_MEDITATION_FOCUS,
  TYPE_TAG_CENTERS,
} from 'services/content-type'
import {
  URL_RECENTLY_ADDED,
  URL_MEDITATION_PRACTICES,
  URL_YOGA_SERIES,
  URL_PILATES_FITNESS,
} from 'services/url/constants'

export const NODE_TAG_TYPE_NEW = 'new'

export function getSafeNodeId (id) {
  // when using JS objects to construct Immutable Maps,
  // that JavaScript Object properties are always strings,
  // even if written in a quote-less shorthand, while Immutable Maps accept keys of any type.
  // Property access for JavaScript Objects first converts the key to a string,
  // but since Immutable Map keys can be of any type the argument to get() is not altered.
  // So for consistency it's much more safe to treat id as string
  return String(id)
}

/**
 * Test if the id is a valid node id
 * @param {*} id The id to test
 * @returns {Boolean} True if this is a valid node Id
 */
export function isValidNodeId (id) {
  return /^[0-9]+$/.test(id)
}

/**
 * An list of tags
 * @param {Object} props - The props
 * @param {Map} props.url - The content url
 * @param {Boolean} props.isNew - Whether or not the content is new (published in the last 7 days)
 * @param {List} props.tags - The list of content tags (references)
 * @param {Map} props.staticText - The static text for the tile.v2 component
 * @param {String} props.contentType - The type of content
 * @param {String} props.userLanguage - The primary language of the user
 * @param {String} props.contentSubtype - The content subtype of the content
 * @param {Boolean} props.hasNewEpisodes - Whether or not the content has new episodes
 * @returns {List} An immutable list of tags
 */
export function getContentTags (props) {
  const {
    url,
    isNew,
    tags,
    staticText,
    contentType,
    userLanguage,
    contentSubtype,
    hasNewEpisodes,
  } = props
  const yogaStyleTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_YOGA_STYLE && t.get('language') === userLanguage)
  const yogaLevelTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_YOGA_LEVEL && t.get('language') === userLanguage)
  const yogaDurationTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_YOGA_DURATION && t.get('language') === userLanguage)
  const yogaFocusTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_YOGA_FOCUS && t.get('language') === userLanguage)
  const fitnessStyleTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_FITNESS_STYLE && t.get('language') === userLanguage)
  const fitnessLevelTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_FITNESS_LEVEL && t.get('language') === userLanguage)
  const fitnessDurationTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_FITNESS_DURATION && t.get('language') === userLanguage)
  const fitnessFocusTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_FITNESS_FOCUS && t.get('language') === userLanguage)
  const meditationStyleTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_MEDITATION_STYLE && t.get('language') === userLanguage)
  const meditationLevelTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_MEDITATION_LEVEL && t.get('language') === userLanguage)
  const meditationDurationTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_MEDITATION_DURATION && t.get('language') === userLanguage)
  const meditationFocusTag = tags.find((t) => t.get('reference_type') === TYPE_TAG_MEDITATION_FOCUS && t.get('language') === userLanguage)
  const genreTags = tags.filter((t) => t.get('reference_type') === TYPE_TAG_GENRE && t.get('language') === userLanguage) || List()
  const centerTags = tags.filter((t) => t.get('reference_type') === TYPE_TAG_CENTERS && t.get('language') === userLanguage) || List()
  let centersGenres = List()
  const derivedTags = List().withMutations((mutatedDerivedTags) => {
    // both videos and series get "new"
    if (isNew) {
      mutatedDerivedTags.push(Map({
        name: staticText.get('new'),
        path: URL_RECENTLY_ADDED,
        type: NODE_TAG_TYPE_NEW,
      }))
    }

    // if this is a series and it has new episodes
    if (hasNewEpisodes && contentType === TYPE_CONTENT_SERIES) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA:
        case SUBTYPE_CONTENT_MEDITATION:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newPractice'),
            path: url,
          }))
          break
        case SUBTYPE_CONTENT_FITNESS:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newWorkout'),
            path: url,
          }))
          break
        default:
          mutatedDerivedTags.push(Map({
            name: staticText.get('newEpisode'),
            path: url,
          }))
          break
      }
    }

    // tags for series
    if (contentType === TYPE_CONTENT_SERIES) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA:
          mutatedDerivedTags.push(Map({
            name: staticText.get('yogaSeries'),
            path: URL_YOGA_SERIES,
          }))
          break
        case SUBTYPE_CONTENT_FITNESS:
          mutatedDerivedTags.push(Map({
            name: staticText.get('fitnessSeries'),
            path: URL_PILATES_FITNESS,
          }))
          break
        case SUBTYPE_CONTENT_MEDITATION:
          mutatedDerivedTags.push(Map({
            name: staticText.get('meditationProgram'),
            path: URL_MEDITATION_PRACTICES,
          }))
          break
        case SUBTYPE_CONTENT_EVENT:
          centersGenres = centersGenres.concat(genreTags)
          break
        default:
          centersGenres = centersGenres.concat(genreTags, centerTags)
          break
      }
    }

    // tags for videos/episodes
    if (contentType === TYPE_CONTENT_VIDEO) {
      switch (contentSubtype) {
        case SUBTYPE_CONTENT_YOGA: {
          if (yogaStyleTag) {
            mutatedDerivedTags.push(yogaStyleTag)
          }

          if (yogaLevelTag) {
            mutatedDerivedTags.push(yogaLevelTag)
          }

          if (yogaDurationTag) {
            mutatedDerivedTags.push(yogaDurationTag)
          }

          if (yogaFocusTag) {
            mutatedDerivedTags.push(yogaFocusTag)
          }
          break
        }
        case SUBTYPE_CONTENT_FITNESS:
          if (fitnessStyleTag) {
            mutatedDerivedTags.push(fitnessStyleTag)
          }

          if (fitnessLevelTag) {
            mutatedDerivedTags.push(fitnessLevelTag)
          }

          if (fitnessDurationTag) {
            mutatedDerivedTags.push(fitnessDurationTag)
          }

          if (fitnessFocusTag) {
            mutatedDerivedTags.push(fitnessFocusTag)
          }
          break
        case SUBTYPE_CONTENT_MEDITATION:
          mutatedDerivedTags.push(Map({
            name: staticText.get('meditation'),
            path: URL_MEDITATION_PRACTICES,
          }))
          if (meditationStyleTag) {
            mutatedDerivedTags.push(meditationStyleTag)
          }

          if (meditationLevelTag) {
            mutatedDerivedTags.push(meditationLevelTag)
          }

          if (meditationDurationTag) {
            mutatedDerivedTags.push(meditationDurationTag)
          }

          if (meditationFocusTag) {
            mutatedDerivedTags.push(meditationFocusTag)
          }
          break
        case SUBTYPE_CONTENT_EVENT:
          centersGenres = centersGenres.concat(genreTags)
          break
        default:
          centersGenres = centersGenres.concat(genreTags, centerTags)
          break
      }
    }

    return mutatedDerivedTags
  })

  // return the top 4 tags
  return derivedTags.concat(centersGenres).slice(0, 4)
}
